import { Component, Inject } from '@angular/core';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { BasicService } from 'src/app/shared/services/basic.service';
import { FacebookService } from 'src/app/shared/strategies/channels';

import { finalize } from 'rxjs/operators';
import { AddAccountService } from './add-account.service';
import { GoogleBusinessProfileService } from './google-business-profile/google-business-profile.service';
import { AuthorizedGoogleAccount } from './shared/models/authorized-google-account.model';

@Component({
  selector: 'app-add-account',
  templateUrl: './add-account.component.html',
  styleUrls: ['./add-account.component.scss'],
})
export class AddAccountComponent {
  descriptionLabel = this.basicService.translate('parameters/accounts/add-account/description', {
    connected: `${this.sitesService.sitesV2.length || 0}`,
    available: `${this.alcmeonContextService.getCompany().max_active_accounts}`,
  });

  strategies: {
    type: string;
    label: string;
    tooltip?: string;
    clickAction?: any;
    icon?: string;
    disable?: any;
  }[] = [
    {
      type: 'facebook-messenger',
      label: this.formatLabel('facebook-messenger'),
      clickAction: () => this.connect('facebook'),
    },
    {
      type: 'facebook-location',
      label: this.formatLabel('facebook-location'),
      clickAction: () => this.facebookLocationsConnect(),
      icon: 'facebook',
      disable: () => !this.sitesService.sitesV2.find(site => site.type === this.facebookService.getType()),
    },
    {
      type: 'whatsapp',
      label: this.formatLabel('whatsapp', { description: 'whatsapp' }),
      clickAction: () => this.whatsappViaVonageConnect(),
    },
    {
      type: 'whatsapp',
      label: this.formatLabel('whatsapp', { description: 'whatsapp-cloud-api' }),
      clickAction: () => this.whatsappCloudAPIConnect(),
      tooltip: this.basicService.translate('parameters/accounts/add-account/whatsapp-cloud-api/tooltip'),
      disable: () => !this.alcmeonContextService.waCloudApiSettings,
    },
    {
      type: 'instagram2',
      label: this.formatLabel('instagram2'),
      clickAction: () => this.connect('instagram2'),
    },
    {
      type: 'twitter',
      icon: 'twitter2', // To remove once blue bird is removed.
      label: this.formatLabel('twitter', { description: 'twitter' }),
      clickAction: () => this.connect('twitter'),
    },
    {
      type: 'webchat',
      label: this.formatLabel('webchat', { description: 'webchat' }),
      clickAction: () => this.connect('webchat'),
    },
    {
      type: 'business-chat',
      label: this.formatLabel('business-chat'),
      tooltip: this.basicService.translate('parameters/accounts/add-account/business-chat/tooltip'),
    },
    {
      type: 'gmb',
      label: this.formatLabel('gmb'),
      clickAction: () => this.getExistingGoogleAccountAttachedToAlcmeon(),
    },
    {
      type: 'line',
      label: this.formatLabel('line'),
      clickAction: () => this.lineConnect(),
    },
    {
      type: 'line',
      label: this.formatLabel('line', { description: 'line-mulesoft' }),
      clickAction: () => this.lineMulesoftConnect(),
    },
    {
      type: 'kakaotalk',
      label: this.formatLabel('kakaotalk'),
      clickAction: () => this.kakaotalkConnect(),
    },
    {
      type: 'trustpilot',
      label: this.formatLabel('trustpilot'),
      clickAction: () => this.connect('trustpilot'),
    },
    {
      type: 'linkedin',
      label: this.formatLabel('linkedin'),
      clickAction: () => this.connect('linkedin'),
    },
    {
      type: 'tiktok',
      label: this.formatLabel('tiktok'),
      clickAction: () => this.connect('tiktok'),
    },
  ];

  error = '';

  constructor(
    public alcmeonContextService: AlcmeonContextService,
    private addAccountService: AddAccountService,
    private basicService: BasicService,
    private customRouter: CustomRouter,
    private facebookService: FacebookService,
    private sitesService: SitesService,
    private googleBusinessProfileService: GoogleBusinessProfileService,
    @Inject('TopToolbarFactory') private topToolbarService: any,
    @Inject('$stateParams') $stateParams: any
  ) {
    this.topToolbarService.setTitleTranslation('parameters/links_menu/accounts', null, {
      uiSref: 'auth.alcmeon.fuse.parameters.accounts',
    });

    let errorKey = '';
    const sites_count = this.sitesService.sitesV2.length;
    const { max_active_accounts } = alcmeonContextService.getCompany();
    if (sites_count >= max_active_accounts && max_active_accounts !== -1) {
      errorKey = 'add-account/error/max-allowed-accounts-reached';
    }
    if ($stateParams.errorId) {
      errorKey = $stateParams.errorId;
    }

    if (errorKey) {
      this.error = this.basicService.translate(errorKey);
    }
  }

  private connect(type) {
    this.addAccountService.connect(type);
  }

  private whatsappViaVonageConnect() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.whatsapp-vonage-register');
  }

  private whatsappCloudAPIConnect() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.whatsapp-cloud-api.pre-qualify');
  }

  private lineConnect() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.line-register');
  }

  private lineMulesoftConnect() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.line-register-mulesoft');
  }

  private kakaotalkConnect() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.kakaotalk-register');
  }

  private facebookLocationsConnect() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.add-account-facebook-locations');
  }

  private formatLabel(type: string, options?: { sameLine?: boolean; description?: string }) {
    let result = `<strong>${this.basicService.translate('parameters/accounts/add-account/' + type + '/name')}</strong>`;
    if (options) {
      const { sameLine, description } = options;
      result += `${sameLine ? ' ' : '<br/>'}<span>${this.basicService.translate(
        'parameters/accounts/add-account/' + description + '/description'
      )}</span>`;
    }
    return result;
  }

  private getExistingGoogleAccountAttachedToAlcmeon() {
    this.basicService.showSpinner();
    this.googleBusinessProfileService
      .getExistingGoogleAccountAttachedToAlcmeon()
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: (result: AuthorizedGoogleAccount) => {
          if (result) {
            this.customRouter.go('auth.alcmeon.fuse.parameters.google-business-profile.login');
          } else {
            this.customRouter.go('auth.alcmeon.fuse.parameters.google-business-profile.first-connection');
          }
        },
      });
  }
}
